<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Sidebar (Handles its own mobile overlay logic) -->
    <HrSidebar v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <!-- md:ml-64 pushes this entire section to the right of the sidebar on desktop. -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar (Sticky, stays at top of this wrapper) -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- Dashboard View -->
        <div v-if="!showContractForm" class="space-y-8">
          
          <!-- Page Header -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Contracts Management</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage employment contracts, renewals, and staff changes</p>
            </div>
          </div>

          <!-- Contract Management Cards -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            
            <!-- New Contract Card -->
            <div 
              @click="navigateTo('contract')" 
              class="group bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md hover:border-blue-200 dark:hover:border-blue-700 transition-all duration-200"
            >
              <div class="flex items-start justify-between mb-4">
                <div class="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">New Employment Contract</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">Create new employment contracts for staff</p>
              <div class="flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 group-hover:translate-x-1 transition-transform">
                <span>Create Contract</span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>

            <!-- Contract Renewal Card -->
            <div 
              @click="navigateTo('renewal')" 
              class="group bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md hover:border-green-200 dark:hover:border-green-700 transition-all duration-200"
            >
              <div class="flex items-start justify-between mb-4">
                <div class="p-3 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
              </div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">Contract Renewal</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">Renew existing employment contracts</p>
              <div class="flex items-center text-sm font-medium text-green-600 dark:text-green-400 group-hover:translate-x-1 transition-transform">
                <span>Manage Renewals</span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>

            <!-- Promotion Card -->
            <div 
              @click="navigateTo('promotion')" 
              class="group bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md hover:border-purple-200 dark:hover:border-purple-700 transition-all duration-200"
            >
              <div class="flex items-start justify-between mb-4">
                <div class="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">Promotion</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">Process staff promotions and role changes</p>
              <div class="flex items-center text-sm font-medium text-purple-600 dark:text-purple-400 group-hover:translate-x-1 transition-transform">
                <span>Process Promotion</span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>

            <!-- Termination Card -->
            <div 
              @click="navigateTo('termination')" 
              class="group bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md hover:border-red-200 dark:hover:border-red-700 transition-all duration-200"
            >
              <div class="flex items-start justify-between mb-4">
                <div class="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
              </div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">Termination</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">Manage contract terminations and exits</p>
              <div class="flex items-center text-sm font-medium text-red-600 dark:text-red-400 group-hover:translate-x-1 transition-transform">
                <span>Process Termination</span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          <!-- Recent Contracts Table -->
          <div class="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Recent Contracts</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Overview of recent contract activities</p>
              </div>
              <button class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
                View All
              </button>
            </div>
            
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Employee</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Position</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Start Date</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">End Date</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th scope="col" class="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  <tr 
                    v-for="contract in recentContracts" 
                    :key="contract.id" 
                    class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="flex items-center">
                        <div class="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                          <span class="text-white text-sm font-semibold">{{ getInitials(contract.employee) }}</span>
                        </div>
                        <div class="ml-4">
                          <div class="text-sm font-medium text-gray-900 dark:text-white">{{ contract.employee }}</div>
                          <div class="text-xs text-gray-500 dark:text-gray-400">{{ contract.employeeId }}</div>
                        </div>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                        {{ contract.type }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm font-medium text-gray-900 dark:text-white">{{ contract.position }}</div>
                      <div v-if="contract.section" class="text-xs text-gray-500 dark:text-gray-400">{{ contract.section }} • {{ contract.class }}</div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ formatDate(contract.startDate) }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ formatDate(contract.endDate) }}</td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="getStatusClass(contract.status)" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold">
                        <span class="w-1.5 h-1.5 rounded-full mr-1.5" :class="getStatusDot(contract.status)"></span>
                        {{ contract.status }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div class="flex items-center justify-end space-x-3">
                        <button 
                          @click="viewContract(contract.id)" 
                          class="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          title="View Details"
                        >
                          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button 
                          @click="editContract(contract.id)" 
                          class="text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                          title="Edit Contract"
                        >
                          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  <!-- Empty State -->
                  <tr v-if="recentContracts.length === 0">
                    <td colspan="7" class="px-6 py-12 text-center">
                      <svg class="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No contracts yet</h3>
                      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new employment contract.</p>
                      <div class="mt-6">
                        <button 
                          @click="navigateTo('contract')"
                          class="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                          </svg>
                          New Contract
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Contract Form View -->
        <div v-else>
          <component
            :is="activeFormComponent"
            :employeeData="selectedEmployeeData"
            :contractType="activeContractType"
            @back="showContractForm = false"
            @submit="handleFormSubmit"
          />
        </div>

      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import HrSidebar from '@/components/hr/HrSidebar.vue';
import HrTopbar from '@/components/hr/HrTopbar.vue';
import EmploymentContract from './EmploymentContract.vue';
import ContractRenewal from './ContractRenewal.vue';
import PromotionForm from './PromotionForm.vue';
import TerminationForm from './TerminationForm.vue';
import { usePermissions } from '@/composables/usePermissions';

const { hasPermission } = usePermissions();

const currentPageTitle = ref('Contracts Management');
const showContractForm = ref(false);
const activeContractType = ref('');
const selectedEmployeeData = ref(null);
const isSidebarOpen = ref(false); // Hidden by default on mobile
const isDarkMode = ref(false);

const activeFormComponent = computed(() => {
  switch(activeContractType.value) {
    case 'contract': return EmploymentContract;
    case 'renewal': return ContractRenewal;
    case 'promotion': return PromotionForm;
    case 'termination': return TerminationForm;
    default: return EmploymentContract;
  }
});

// Sample employees data
const employees = ref([
  { id: 'EMP001', name: 'Kwame Mensah', position: 'Mathematics Teacher', department: 'Academics', section: 'Secondary', currentClass: 'Form 2', contractEndDate: '2024-06-30' },
  { id: 'EMP002', name: 'Ama Serwaa', position: 'English Tutor', department: 'Academics', section: 'Primary', currentClass: 'Class 4', contractEndDate: '2023-12-31' },
  { id: 'EMP003', name: 'Yaw Boateng', position: 'Administrator', department: 'Administration', contractEndDate: '2023-10-31' }
]);

// Sample contracts data - replace with API calls in production
const recentContracts = ref([
  {
    id: 'CT001',
    employee: 'Kwame Mensah',
    employeeId: 'EMP001',
    type: 'Full-Time',
    position: 'Mathematics Teacher',
    section: 'Secondary',
    class: 'Form 2',
    startDate: '2023-01-15',
    endDate: '2024-01-14',
    status: 'Active'
  },
  {
    id: 'CT002',
    employee: 'Ama Serwaa',
    employeeId: 'EMP002',
    type: 'Part-Time',
    position: 'English Tutor',
    section: 'Primary',
    class: 'Class 4',
    startDate: '2023-03-01',
    endDate: '2023-12-31',
    status: 'Pending Renewal'
  },
  {
    id: 'CT003',
    employee: 'Yaw Boateng',
    employeeId: 'EMP003',
    type: 'Contract',
    position: 'Administrator',
    section: '',
    class: '',
    startDate: '2022-11-01',
    endDate: '2023-10-31',
    status: 'Expired'
  }
]);

// Navigation
const navigateTo = (type) => {
  activeContractType.value = type;
  showContractForm.value = true;
};

const viewContract = (id) => {
  console.log('View contract:', id);
  // TODO: Implement view contract logic
};

const editContract = (id) => {
  console.log('Edit contract:', id);
  // TODO: Implement edit contract logic
};

const handleFormSubmit = (formData) => {
  console.log(`${activeContractType.value} submitted:`, formData);
  showContractForm.value = false;
  // TODO: Add to recentContracts or update existing
};

// Helper functions
const getInitials = (name) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

const getStatusClass = (status) => {
  const statusLower = status.toLowerCase();
  switch(statusLower) {
    case 'active': 
      return 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'pending renewal': 
      return 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'expired': 
      return 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    default: 
      return 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }
};

const getStatusDot = (status) => {
  const statusLower = status.toLowerCase();
  switch(statusLower) {
    case 'active': return 'bg-green-500';
    case 'pending renewal': return 'bg-yellow-500';
    case 'expired': return 'bg-red-500';
    default: return 'bg-gray-500';
  }
};

// Sidebar & Dark Mode
const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
};

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark');
    localStorage.theme = 'dark';
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.theme = 'light';
  }
};
</script>

<style scoped>
/* Smooth transitions for dark mode */
.bg-white, .bg-gray-50, .bg-gray-800, .bg-gray-900 {
  transition: background-color 0.3s ease;
}
.text-gray-900, .text-gray-500, .text-gray-400 {
  transition: color 0.3s ease;
}
.border-gray-100, .border-gray-200, .border-gray-700 {
  transition: border-color 0.3s ease;
}
</style>